import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  Notice,
  Panel,
  Select,
  Spinner,
  Table,
  TableWrap,
  Textarea,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Category } from '../api';
import { openRemoteSupport } from '../remoteSupport';

/**
 * Machines running iBoon Agent, and who each one belongs to.
 *
 * This is the half of MeshCentral that was worth keeping. A device claims
 * itself the first time somebody signs in on it and stays theirs until
 * released here -- so this page is the only way a laptop changes hands, and
 * the only place the specs it reported can be read.
 *
 * A released device is kept, not deleted. Who had a machine is exactly the
 * thing somebody asks six months later.
 */
type Device = {
  id: string;
  /** 'phone' when iBoon Agent for phones claimed it; absent on older servers. */
  kind?: 'computer' | 'phone';
  hostname: string;
  platform: string;
  osVersion: string;
  agentVersion: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  specs: {
    cpu?: string;
    cores?: number;
    memoryBytes?: number;
    disks?: Array<{ name: string; kind: string; totalBytes: number }>;
  };
  firstSeenAt: string;
  lastSeenAt: string;
  /** The Agent on it holds a live socket to this server right now. */
  online: boolean;
  deregisteredAt: string | null;
  deregisteredBy: string | null;
  personId: string;
  personName: string;
  personEmail: string;
  assetId: string | null;
  assetName: string | null;
};

const gb = (bytes: number): string => `${Math.round(bytes / 1024 ** 3)} GB`;

const ago = (iso: string): string => {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 1) return 'just now';
  if (hours < 36) return `${Math.round(hours)} hours ago`;
  const days = Math.round(hours / 24);
  return days < 14 ? `${days} days ago` : `${Math.round(days / 7)} weeks ago`;
};

/** The one-line summary somebody would actually recognise the machine by. */
function describe(specs: Device['specs']): string {
  const parts: string[] = [];
  if (specs.cpu) parts.push(specs.cpu);
  if (specs.memoryBytes) parts.push(`${gb(specs.memoryBytes)} RAM`);
  const storage = (specs.disks ?? []).reduce((sum, disk) => sum + disk.totalBytes, 0);
  if (storage) parts.push(`${gb(storage)} storage`);
  return parts.join(' · ');
}

export function Devices() {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeReleased, setIncludeReleased] = useState(false);
  const [releasing, setReleasing] = useState<Device | null>(null);
  const [adopting, setAdopting] = useState<Device | null>(null);
  const [reason, setReason] = useState('');
  const toast = useToast();

  const load = useCallback(async (withReleased: boolean) => {
    const result = await api.get<{ devices: Device[] }>(
      `/api/devices${withReleased ? '?includeReleased=true' : ''}`,
    );
    setDevices(result.devices);
  }, []);

  useEffect(() => {
    setDevices(null);
    load(includeReleased).catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load devices.'),
    );
  }, [includeReleased, load]);

  async function release() {
    if (!releasing) return;
    try {
      await api.post(`/api/devices/${releasing.id}/deregister`, { reason });
      toast.show(
        `${releasing.hostname || 'That machine'} is released. The next person to sign in claims it.`,
      );
      setReleasing(null);
      setReason('');
      await load(includeReleased);
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not release it.', {
        tone: 'danger',
      });
    }
  }

  if (error) {
    return (
      <Notice tone="danger" title="Could not load devices">
        {error}
      </Notice>
    );
  }

  const live = (devices ?? []).filter((device) => !device.deregisteredAt);

  return (
    <>
      <Toolbar>
        <span className="sub">
          {live.length} {live.length === 1 ? 'machine' : 'machines'} registered
        </span>
        <Button variant="ghost" onClick={() => setIncludeReleased((was) => !was)}>
          {includeReleased ? 'Hide released' : 'Show released'}
        </Button>
      </Toolbar>

      {!devices ? (
        <Spinner />
      ) : devices.length === 0 ? (
        <EmptyState
          title="No machines yet"
          description="A device appears here the first time somebody signs into iBoon Agent on it."
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Whose</th>
                  <th>Specification</th>
                  <th>In the register</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id}>
                    <td className="tcell-name">
                      <Link to={`/devices/${device.id}`}>
                        {device.hostname || <span className="muted">unnamed</span>}
                      </Link>
                      <div className="sub">
                        {[device.kind === 'phone' ? 'Phone' : null, device.platform, device.osVersion]
                          .filter(Boolean)
                          .join(' · ')}
                        {device.agentVersion && ` · Agent ${device.agentVersion}`}
                      </div>
                    </td>
                    <td>
                      {device.personName}
                      <div className="sub">{device.personEmail}</div>
                    </td>
                    <td className="sub">{describe(device.specs) || '—'}</td>
                    {/*
                      * A device is not an asset, and this column is where that
                      * stops being confusing. A machine can be registered to
                      * somebody and still be a thing the company has no record
                      * of buying -- which read as "they have nothing" on the
                      * People page until this said so out loud.
                      */}
                    <td>
                      {device.assetName ? (
                        <span className="sub">{device.assetName}</span>
                      ) : (
                        <Badge tone="neutral">not an asset yet</Badge>
                      )}
                    </td>
                    <td>
                      {device.deregisteredAt ? (
                        <Badge tone="neutral">released</Badge>
                      ) : device.online ? (
                        // Live from the pulse socket, not a stale stamp: the
                        // Agent is connected to this server this second.
                        <Badge tone="brand">online now</Badge>
                      ) : (
                        ago(device.lastSeenAt)
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        {!device.deregisteredAt && device.online && (
                          <Button variant="ghost" onClick={() => openRemoteSupport(device.id, device.personEmail)}>
                            Remote support
                          </Button>
                        )}
                        {!device.deregisteredAt && !device.assetId && (
                          <Button variant="ghost" onClick={() => setAdopting(device)}>
                            Add to the register
                          </Button>
                        )}
                        {!device.deregisteredAt && (
                          <Button variant="ghost" onClick={() => setReleasing(device)}>
                            Release
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <Panel title="How this works">
        <p className="sub">
          A machine claims itself the first time somebody signs into iBoon Agent on it, and belongs
          to that person from then on — anybody else signing in on it is refused and told whose it
          is. One person can hold several machines; the rule is about the machine.
        </p>
        <p className="sub">
          Releasing one here is the only way it changes hands. The row is kept rather than deleted,
          so who had a laptop is still answerable later.
        </p>
        <p className="sub">
          A registered machine is not the same as an asset. This list is what the Agent has seen;
          the register is what the company recorded buying, with a tag, a price and a warranty.
          <strong> Add to the register</strong> makes the second from the first, with everything the
          machine already reported filled in.
        </p>
      </Panel>

      {adopting && (
        <Adopt
          device={adopting}
          onClose={() => setAdopting(null)}
          onDone={() => {
            setAdopting(null);
            void load(includeReleased);
          }}
        />
      )}

      {releasing && (
        <ConfirmDialog
          open
          danger
          title={`Release ${releasing.hostname || 'this machine'}?`}
          confirmLabel="Release it"
          onConfirm={() => void release()}
          onClose={() => {
            setReleasing(null);
            setReason('');
          }}
        >
          <p>
            It stops belonging to {releasing.personName}, and the next person to sign in on it
            claims it. The record of them having had it is kept.
          </p>
          <Field label="Why (optional)" hint="Recorded against the device.">
            <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
        </ConfirmDialog>
      )}
    </>
  );
}

/**
 * Recording a machine as a thing the company owns.
 *
 * The category is the only thing that has to be asked for: everything else --
 * make, model, serial -- the machine already said, and asking somebody to
 * retype what is on the screen in front of them is how a register stops being
 * filled in.
 */
function Adopt({
  device,
  onClose,
  onDone,
}: {
  device: Device;
  onClose: () => void;
  onDone: () => void;
}) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ categories: Category[] }>('/api/categories')
      .then((r) => {
        const tracked = r.categories.filter((c) => c.tracking === 'tracked');
        setCategories(tracked);
        setCategoryId(tracked[0]?.id ?? '');
      })
      .catch(() => setCategories([]));
  }, []);

  async function save() {
    setBusy(true);
    try {
      const result = await api.post<{ name: string; heldBy: string }>(
        `/api/devices/${device.id}/adopt`,
        { categoryId },
      );
      toast.show(`${result.name} is in the register, held by ${result.heldBy}.`);
      onDone();
    } catch (caught) {
      // The refusal names the asset that already has this serial, which is the
      // thing somebody needs in order to link rather than duplicate.
      toast.show(caught instanceof Error ? caught.message : 'Could not add it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title={`Add ${device.hostname} to the register`} onClose={onClose}>
      {categories.length === 0 ? (
        <Notice tone="accent" title="No categories yet">
          An asset belongs to a category — Laptops, Desktops. Add one on the Catalog page first.
        </Notice>
      ) : (
        <>
          <Field label="What kind of thing is it" required>
            <Select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>

          <Panel title="Taken from what the machine reported">
            <div className="line">
              <span className="line__label">Name</span>
              <span>{device.hostname}</span>
            </div>
            <div className="line">
              <span className="line__label">Make</span>
              <span>{device.manufacturer ?? <span className="muted">not reported</span>}</span>
            </div>
            <div className="line">
              <span className="line__label">Model</span>
              <span>{device.model ?? <span className="muted">not reported</span>}</span>
            </div>
            <div className="line">
              <span className="line__label">Serial</span>
              <span>{device.serialNumber ?? <span className="muted">not reported</span>}</span>
            </div>
          </Panel>

          <Notice tone="accent" title={`It will be recorded as ${device.personName}’s`}>
            They are holding it already. Recording it as free on the shelf would be the one thing
            this register exists to avoid.
          </Notice>

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !categoryId} onClick={() => void save()}>
              {busy ? 'Adding…' : 'Add it'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
